import { useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { useStore } from '../hooks/useStore';
import { getLeaderboard, type LeaderboardSort } from '../services/leagueAnalyticsService';

const SORT_LABELS: Record<LeaderboardSort, string> = {
  points: 'Points',
  goals: 'Goals',
  assists: 'Assists',
  shots: 'Shots',
  penaltyMinutes: 'Fewest PIM',
  shootoutGoals: 'Shootout goals',
};

export function LeagueLeaderboardPage() {
  const db = useStore((d) => d);
  const [competitionId, setCompetitionId] = useState<string>('');
  const [sort, setSort] = useState<LeaderboardSort>('points');

  const rows = useMemo(
    () => getLeaderboard({ competitionId: competitionId || undefined, sort, limit: 100 }),
    // re-evaluate on store changes
    [db, competitionId, sort],
  );

  return (
    <>
      <ModuleHero
        eyebrow="League"
        title="Leaderboards"
        description="Cross-competition leaders for goals, assists, points, shots, and discipline. Filter to a single event or roll the whole league up."
        icon={Trophy}
        accent="arc"
      />
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <label>
            <span style={{ marginRight: 8 }}>Competition:</span>
            <select value={competitionId} onChange={(e) => setCompetitionId(e.target.value)}>
              <option value="">All competitions</option>
              {db.tournaments.filter((t) => t.status !== 'archived').map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={{ marginRight: 8 }}>Sort by:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as LeaderboardSort)}>
              {(Object.keys(SORT_LABELS) as LeaderboardSort[]).map((k) => (
                <option key={k} value={k}>{SORT_LABELS[k]}</option>
              ))}
            </select>
          </label>
        </div>

        {rows.length === 0 ? (
          <div className="empty">No stats logged yet — record some goals/assists to populate this board.</div>
        ) : (
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Player</th>
                <th style={th}>Team</th>
                <th style={thNum}>GP</th>
                <th style={thNum}>G</th>
                <th style={thNum}>A</th>
                <th style={thNum}>P</th>
                <th style={thNum}>S</th>
                <th style={thNum}>PIM</th>
                <th style={thNum}>GWG</th>
                <th style={thNum}>SO</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.profileId}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{r.displayName}</td>
                  <td style={td}>{r.teamName ?? '—'}</td>
                  <td style={tdNum}>{r.gamesPlayed}</td>
                  <td style={tdNum}>{r.goals}</td>
                  <td style={tdNum}>{r.assists}</td>
                  <td style={tdNum}>{r.points}</td>
                  <td style={tdNum}>{r.shots}</td>
                  <td style={tdNum}>{r.penaltyMinutes}</td>
                  <td style={tdNum}>{r.gameWinningGoals}</td>
                  <td style={tdNum}>{r.shootoutAttempts > 0 ? `${r.shootoutGoals}/${r.shootoutAttempts}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--surface-border, #2a2a2a)' };
const thNum: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--surface-border, #2a2a2a)' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
